import { useMemo } from 'react';
import { useUserRole } from './useUserRole';

// Single source of truth for authorization in the dashboard. Everything (route
// guards, navigation visibility, page-level gating) derives from the user's
// roles in `user_church_roles` (read via useUserRole). No hardcoded permission
// tables — the previous implementation queried `permissions`/`roles` tables that
// do not exist.

// Canonical roles, highest privilege first. `staff` is treated as `editor`.
export type AppRole = 'super_admin' | 'church_admin' | 'editor' | 'member' | 'public';

const ROLE_RANK: Record<AppRole, number> = {
  super_admin: 4,
  church_admin: 3,
  editor: 2,
  member: 1,
  public: 0,
};

function normalizeRole(raw: string | undefined | null): AppRole {
  switch ((raw || '').toLowerCase()) {
    case 'super_admin':
      return 'super_admin';
    case 'church_admin':
      return 'church_admin';
    case 'editor':
    case 'staff':
      return 'editor';
    case 'member':
      return 'member';
    default:
      // An authenticated user with an unrecognized role gets least privilege.
      return 'member';
  }
}

// Coarse capabilities checked by guards, navigation, and features.
export type Capability =
  | 'platform.admin' // super-admin only: platform users/settings/audit/integrations
  | 'church.manage' // church profile, admins, settings
  | 'members.manage'
  | 'communications.send' // sms / announcements / chat moderation
  | 'giving.manage'
  | 'analytics.view'
  | 'directory.view' // member-level
  | 'community.participate'; // member-level: prayer, events, chat

const ROLE_CAPABILITIES: Record<AppRole, Capability[]> = {
  super_admin: [
    'platform.admin', 'church.manage', 'members.manage', 'communications.send',
    'giving.manage', 'analytics.view', 'directory.view', 'community.participate',
  ],
  church_admin: [
    'church.manage', 'members.manage', 'communications.send', 'giving.manage',
    'analytics.view', 'directory.view', 'community.participate',
  ],
  editor: ['members.manage', 'communications.send', 'directory.view', 'community.participate'],
  member: ['directory.view', 'community.participate'],
  public: [],
};

export interface PermissionContext {
  loading: boolean;
  /** Highest role the user holds across all churches. */
  role: AppRole;
  isSuperAdmin: boolean;
  isChurchAdmin: boolean; // church_admin in at least one church (or super_admin)
  isMember: boolean; // authenticated with at least one role
  /** Church ids the user administers as church_admin. */
  churchIds: string[];
  /** True if the user is super_admin, or church_admin of the given church. */
  administersChurch: (churchId?: string | null) => boolean;
  /** Capability check based on the user's highest role. */
  can: (capability: Capability) => boolean;
}

export function usePermissions(): PermissionContext {
  const { roles, isSuperAdmin, loading } = useUserRole();

  return useMemo<PermissionContext>(() => {
    const roleRows = Array.isArray(roles) ? roles : [];

    const highest: AppRole = isSuperAdmin
      ? 'super_admin'
      : roleRows.reduce<AppRole>((hi, r) => {
          const norm = normalizeRole(r.role);
          return ROLE_RANK[norm] > ROLE_RANK[hi] ? norm : hi;
        }, roleRows.length > 0 ? 'member' : 'public');

    const churchIds = roleRows
      .filter((r) => normalizeRole(r.role) === 'church_admin')
      .map((r) => r.church_id);

    const capabilities = ROLE_CAPABILITIES[highest];

    return {
      loading,
      role: highest,
      isSuperAdmin,
      isChurchAdmin: isSuperAdmin || churchIds.length > 0,
      isMember: roleRows.length > 0,
      churchIds,
      administersChurch: (churchId) =>
        isSuperAdmin || (!!churchId && churchIds.includes(churchId)),
      can: (capability) => capabilities.includes(capability),
    };
  }, [roles, isSuperAdmin, loading]);
}

// Backward-compatible page-level gate. All current call sites (attendance,
// budgets, communications, contributions) gate church-management features, which
// require an admin role. Kept as a thin wrapper over the single source so pages
// don't need to change. `action` is retained for signature compatibility.
export function useHasAccess(
  _module: string,
  _action: 'view' | 'edit' | 'delete' = 'view',
): boolean {
  const { isSuperAdmin, isChurchAdmin, loading } = usePermissions();
  if (loading) return false;
  return isSuperAdmin || isChurchAdmin;
}
